import { audit, distinctUntilChanged, finalize, first, map, shareReplay, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { compareState, deserialize, serialize } from './session-persistence.service.utils';
import { inject, injectable } from 'inversify';
import { interval, merge, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { IPersistenceAdapterService, PERSISTENCE_ADAPTER_SERVICE } from '../message-adapters/persistence-adapter';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../session-lifecycle';
import { ISessionPersistenceService } from './session-persistence.interface';
import { IStateObject } from '../../interfaces';
import { STATE_KEYS } from '../../enums';

const SAVE_INTERVAL = 3000;

@injectable()
export class SessionPersistenceService implements ISessionPersistenceService {

    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;
    @inject(PERSISTENCE_ADAPTER_SERVICE) private readonly adapter: IPersistenceAdapterService;

    private readonly saveCommandObs = new Subject<boolean>();

    private stateObs: Subscription;

    private readonly fullState = new ReplaySubject<IStateObject>(1);

    forceSave = (): void => this.saveCommandObs.next(true);

    stateObservable = (): Observable<IStateObject> => {

        this.initState();

        return this.adapter.settingsObs(STATE_KEYS).pipe(
            first(),
            takeUntil(this.sessionLifeCycle.destroyObservable()),
            map(deserialize(STATE_KEYS)),
        );
    };

    save = (state: IStateObject): void => this.initState() && this.fullState.next(state);

    private readonly initState = () =>
        this.stateObs ?? (
            this.stateObs =
            this.fullState.pipe(
                audit(() => merge(
                    interval(SAVE_INTERVAL),
                    this.saveCommandObs,
                )),
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                finalize(() => this.stateObs = null),
                withLatestFrom(this.fullState),
                map(([ , state ]) => state),
                map(serialize(STATE_KEYS)),
                distinctUntilChanged(compareState(STATE_KEYS)),
                tap(this.adapter.save),
                shareReplay(1),
            ).subscribe());
}
